import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import { fetchProtocolPools } from "../services/defiService";

// 格式化 APY 显示
function formatApy(apy) {
  if (apy === null || apy === undefined) return "N/A";
  // 确保转换为数字类型（PostgreSQL DECIMAL 可能返回字符串）
  const apyNum = typeof apy === 'number' ? apy : parseFloat(apy);
  if (isNaN(apyNum)) return "N/A";
  return `${apyNum.toFixed(2)}%`;
}

// 格式化 TVL 显示
function formatTvl(tvl) {
  if (!tvl || tvl === 0) return "N/A";
  if (tvl >= 1e9) return `$${(tvl / 1e9).toFixed(2)}B`;
  if (tvl >= 1e6) return `$${(tvl / 1e6).toFixed(2)}M`;
  if (tvl >= 1e3) return `$${(tvl / 1e3).toFixed(2)}K`;
  return `$${tvl.toFixed(2)}`;
}

// 格式化时间
function formatTimeAgo(timestamp) {
  if (!timestamp) return "Unknown";
  
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return "Unknown";
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return "just now";
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? "1 minute ago" : `${diffInMinutes} minutes ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
    }
    
    return date.toLocaleDateString();
  } catch (error) {
    return "Unknown";
  }
}

function Pools({ slug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("apy"); // apy, tvl, chain, token
  const [filterChain, setFilterChain] = useState("");
  const [showChainDropdown, setShowChainDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [chainSearch, setChainSearch] = useState("");
  const chainDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  useEffect(() => {
    async function loadPools() {
      try {
        setLoading(true);
        setError(null);
        console.log('[Pools] Loading pools for slug:', slug);
        const result = await fetchProtocolPools(slug);
        console.log('[Pools] Received data:', result);
        if (result.success === false || result.error) {
          throw new Error(result.error || result.message || "Failed to load pools");
        }
        setData(result);
      } catch (err) {
        console.error('[Pools] Error loading pools:', err);
        setError(err.message || "Failed to load pools");
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      loadPools();
    }
  }, [slug]);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chainDropdownRef.current && !chainDropdownRef.current.contains(event.target)) {
        setShowChainDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 flex justify-center items-center">
          <div className="text-gray-600">Loading pools...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 flex justify-center items-center">
          <div className="text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  if (!data || !data.protocol) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 flex justify-center items-center">
          <div className="text-gray-600">Protocol not found</div>
        </div>
      </div>
    );
  }

  const { protocol, pools } = data;

  // 获取所有链列表
  const allChains = [...new Set(pools.map(p => p.chain).filter(Boolean))].sort();
  
  // 过滤链列表（根据搜索关键词）
  const filteredChains = chainSearch
    ? allChains.filter(chain => chain.toLowerCase().includes(chainSearch.toLowerCase()))
    : allChains;
  
  // 排序选项
  const sortOptions = [
    { value: "apy", label: "APY (High to Low)" },
    { value: "tvl", label: "TVL (High to Low)" },
    { value: "chain", label: "Chain" },
    { value: "token", label: "Token" },
  ];

  // 过滤和排序 pools
  let filteredPools = pools;
  
  if (filterChain) {
    filteredPools = filteredPools.filter(p => p.chain === filterChain);
  }

  // 排序
  filteredPools = [...filteredPools].sort((a, b) => {
    switch (sortBy) {
      case "apy":
        return (b.apy || 0) - (a.apy || 0);
      case "tvl":
        const tvlA = a.pool_metadata?.tvlUsd || 0;
        const tvlB = b.pool_metadata?.tvlUsd || 0;
        return tvlB - tvlA;
      case "chain":
        return (a.chain || "").localeCompare(b.chain || "");
      case "token":
        return (a.token_symbol || "").localeCompare(b.token_symbol || "");
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Protocol Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-start gap-4">
            {protocol.logo && (
              <img
                src={protocol.logo}
                alt={`${protocol.name} logo`}
                className="w-16 h-16 rounded-xl border border-gray-200 object-cover bg-gray-50"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect width='64' height='64' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='24' fill='%239ca3af'%3E" + (protocol.name?.charAt(0)?.toUpperCase() || '?') + "%3C/text%3E%3C/svg%3E";
                }}
              />
            )}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{protocol.name}</h1>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
                  {protocol.category}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                  {protocol.blockchain}
                </span>
              </div>
            </div>
            <a
              href={`/report/${encodeURIComponent(protocol.slug || slug)}`}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              View Report
            </a>
          </div>
        </div>

        {/* Filters and Sort */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-stretch">
            {/* Chain 可搜索下拉 */}
            <div className="flex-1 min-w-[200px] relative" ref={chainDropdownRef}>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                Filter by Chain
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowChainDropdown(!showChainDropdown);
                  setShowSortDropdown(false);
                }}
                className="w-full h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between"
              >
                <span>{filterChain || "All Chains"}</span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${showChainDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showChainDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  <div className="p-2 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder="Search chains..."
                      value={chainSearch}
                      onChange={(e) => setChainSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-64">
                    <button
                      type="button"
                      onClick={() => {
                        setFilterChain("");
                        setShowChainDropdown(false);
                        setChainSearch("");
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${!filterChain ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      All Chains
                    </button>
                    {filteredChains.length > 0 ? (
                      filteredChains.map((chain) => (
                        <button
                          key={chain}
                          type="button"
                          onClick={() => {
                            setFilterChain(chain);
                            setShowChainDropdown(false);
                            setChainSearch("");
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${filterChain === chain ? 'bg-blue-50 text-blue-600' : ''}`}
                        >
                          {chain}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-500">No chains found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Sort By 可搜索下拉 */}
            <div className="flex-1 min-w-[200px] relative" ref={sortDropdownRef}>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                Sort By
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowSortDropdown(!showSortDropdown);
                  setShowChainDropdown(false);
                }}
                className="w-full h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between"
              >
                <span>{sortOptions.find(opt => opt.value === sortBy)?.label || "APY (High to Low)"}</span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showSortDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  <div className="overflow-y-auto max-h-64">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setSortBy(option.value);
                          setShowSortDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortBy === option.value ? 'bg-blue-50 text-blue-600' : ''}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pools List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">
              APY Pools ({filteredPools.length})
            </h2>
          </div>

          {filteredPools.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              No pools found
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredPools.map((pool, index) => {
                const metadata = pool.pool_metadata || {};
                const tvl = metadata.tvlUsd || null;
                const apyBase = metadata.apyBase || null;
                const apyReward = metadata.apyReward || null;

                return (
                  <div key={pool.id || index} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Token Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {pool.token_symbol || "Unknown Token"}
                          </h3>
                          {pool.token_name && pool.token_name !== pool.token_symbol && (
                            <span className="text-sm text-gray-500">({pool.token_name})</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {pool.chain && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                              {pool.chain}
                            </span>
                          )}
                          {pool.project_name && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                              {pool.project_name}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* APY Info */}
                      <div className="flex-shrink-0">
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600 mb-1">
                            {formatApy(pool.apy)}
                          </div>
                          {(apyBase !== null || apyReward !== null) && (
                            <div className="text-xs text-gray-500 space-y-0.5">
                              {apyBase !== null && (
                                <div>Base: {formatApy(apyBase)}</div>
                              )}
                              {apyReward !== null && (
                                <div>Reward: {formatApy(apyReward)}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* TVL and Updated */}
                      <div className="flex-shrink-0 text-right">
                        <div className="text-sm font-semibold text-gray-900 mb-1">
                          {formatTvl(tvl)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Updated {formatTimeAgo(pool.updated_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Pools;

